import { Stack, Typography, useTheme } from '@mui/material';
import { Section, SectionRow } from '../../../interfaces';
import { MarketingCampaignWithSource } from '../../../types/marketing';
import SideBarModal from '../../modal/SideBarModal';
import {
  campaignHasEmailStatistics,
  getCampaignLastEditedText,
  getCampaignMediumsText,
} from '../../../helpers/marketing';
import CampaignBadge from '../CampaignBadge';
import { pluralize } from '../../../utils/global/helper';
import CampaignPreviewImage from './CampaignPreviewImage';

interface Props {
  isOpen: boolean;
  campaign: MarketingCampaignWithSource | null;
  onClose: () => void;
}

function CampaignSideBarModal({ isOpen, onClose, campaign }: Props) {
  const theme = useTheme();

  if (!campaign) return null;

  const {
    source: { statistics },
  } = campaign;

  const hasEmailStatistics = campaignHasEmailStatistics(campaign);
  const editedText = getCampaignLastEditedText(campaign);
  const customersCount =
    campaign.source.statistics.total_customers_enrolled || 0;
  const sentCount =
    (hasEmailStatistics
      ? statistics?.emails?.sent
      : statistics.total_customers_finished) || 0;

  const metrics = [
    {
      label: 'Audience Size',
      type: 'TEXT',
      value: [
        `${customersCount || 0} ${pluralize('customer', customersCount)}`,
      ],
    },
    {
      label: 'Total Sent',
      type: 'TEXT',
      value: [`${sentCount || 0} `],
    },
    ...(hasEmailStatistics
      ? ([
          {
            label: 'Total Delivered',
            type: 'TEXT',
            value: [`${statistics.emails?.delivered || 0} `],
          },
          {
            label: 'Number of Opens',
            type: 'TEXT',
            value: [`${statistics.emails?.opened || 0} `],
          },
          {
            label: 'Hard Bounces',
            type: 'TEXT',
            value: [`${statistics.emails?.hard_bounced || 0} `],
          },
          {
            label: 'Soft Bounces',
            type: 'TEXT',
            value: [`${statistics.emails?.soft_bounced || 0} `],
          },
          statistics.emails?.clicked !== null && {
            label: 'Clicked',
            type: 'TEXT',
            value: [`${statistics.emails?.clicked || 0} `],
          },
        ].filter(Boolean) as SectionRow[])
      : []),
  ].filter(Boolean) as SectionRow[];

  const sections: Section[] = [
    {
      name: 'Overview',
      rows: [
        {
          type: 'NODE',
          label: '',
          value: [
            <Stack gap={0.5}>
              <Typography variant="h5" fontWeight="bold">
                {campaign.name}
              </Typography>
              <Typography variant="body2">
                {getCampaignMediumsText(campaign.mediums)}
              </Typography>
              {editedText && (
                <Typography variant="body2" color={theme.palette.medGrey.main}>
                  {editedText}
                </Typography>
              )}
              <CampaignBadge campaign={campaign} />
            </Stack>,
          ],
        },
      ],
    },
    {
      name: 'Metrics',
      rows: metrics,
    },
    campaign.source.steps.length > 0 && {
      name: 'Content',
      rows: [
        {
          label: '',
          type: 'NODE',
          value: [<CampaignPreviewImage campaign={campaign} />],
        },
      ],
    },
  ].filter(Boolean) as Section[];

  return (
    <SideBarModal
      modalOpen={isOpen}
      name="Campaign Details"
      sections={sections}
      setModalOpen={onClose}
    />
  );
}

export default CampaignSideBarModal;
